import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { AppShell } from "@/components/layout/AppShell";
import { useAuthContext } from "@/contexts/AuthContext";
import { Button, Input } from "@/components/ui";
import { PressEyebrow } from "@/components/pullpress/PressVisuals";
import { consumePostAuthRoute } from "@/lib/authRedirect";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signInWithEmail, signInWithGoogle, resetPassword, error, clearError } = useAuthContext();
  const packageIntent = normalizePackageIntent(searchParams.get("package"));
  const postAuthRoute = packageIntent ? `/new-project?package=${packageIntent}` : authRedirectPath(location.state) ?? "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    navigate(consumePostAuthRoute() ?? postAuthRoute, { replace: true });
  }, [navigate, postAuthRoute, user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setNotice(null);
    try {
      setSubmitting(true);
      await signInWithEmail(email, password);
      navigate(postAuthRoute);
    } catch {
      /* Auth context owns the rendered error state. */
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    clearError();
    setNotice(null);
    setGoogleSubmitting(true);
    try {
      const signedInUser = await signInWithGoogle(postAuthRoute);
      if (!signedInUser) return;
      navigate(postAuthRoute);
    } catch {
      /* Auth context owns the rendered error state. */
    } finally {
      setGoogleSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    clearError();
    setNotice(null);
    try {
      await resetPassword(email);
      setNotice("We sent reset instructions to your email.");
    } catch {
      /* Auth context owns the rendered error state. */
    }
  };

  return (
    <AppShell variant="minimal">
      <div className="relative min-h-[calc(100dvh-64px)] overflow-hidden px-4 py-8 sm:px-6 lg:px-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-10 mx-auto h-[480px] max-w-[860px] rounded-full bg-[radial-gradient(circle,var(--as-paper-raised)_0%,var(--as-walnut-tint)_34%,transparent_70%)] opacity-70 blur-3xl"
        />
        <div className="relative mx-auto grid min-h-[calc(100dvh-128px)] w-full min-w-0 max-w-[960px] gap-8 lg:grid-cols-[minmax(0,0.75fr)_minmax(360px,420px)] lg:items-center">
          <aside className="hidden lg:block">
            <PressEyebrow>PullPress</PressEyebrow>
            <h1 className="mt-5 max-w-[480px] font-display text-[clamp(3rem,5vw,5.2rem)] font-semibold leading-[0.94] tracking-as-normal text-ink-1">
              Return to the work.
            </h1>
            <p className="mt-6 max-w-[390px] font-text text-lg leading-as-read text-ink-2">
              Open the manuscript, check the next step, and keep the book moving.
            </p>
          </aside>

          <section className="w-[min(360px,calc(100vw-32px))] min-w-0 justify-self-start overflow-hidden rounded-[32px] border border-[color:color-mix(in_oklab,var(--as-rule)_50%,transparent)] bg-paper-raised/78 p-5 shadow-as-2 backdrop-blur-2xl sm:w-[min(420px,calc(100vw-48px))] sm:justify-self-center sm:p-7 lg:w-full lg:justify-self-stretch">
            <p className="font-ui text-sm font-semibold text-ink-3">Sign in.</p>
            <h2 className="mt-3 font-display text-[clamp(1.9rem,4vw,2.75rem)] font-semibold leading-tight tracking-as-normal text-ink-1">
              Open your workspace.
            </h2>
            <p className="mt-2 max-w-[360px] font-text text-base leading-as-read text-ink-2">
              Use the email and password for your PullPress account.
            </p>

            {(error || notice) && (
              <div className={`mt-5 rounded-as-2 border p-3 font-ui text-sm leading-as-read ${error ? "border-flag/30 bg-flag-tint text-flag" : "border-verify/30 bg-verify-tint text-verify"}`}>
                {error ?? notice}
              </div>
            )}

          <form onSubmit={handleSubmit} className="mt-6 min-w-0 space-y-4">
            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com"
              autoComplete="email"
              required
              className="h-12 text-base"
            />
            <div>
              <div className="mb-1 flex items-center justify-between gap-4">
                <label htmlFor="password" className="font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3">
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  className="inline-flex min-h-10 items-center font-text text-sm text-walnut-deep underline-offset-2 hover:underline"
                >
                  Reset password
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  className="h-12 w-full rounded-as-2 border border-rule-faint bg-paper-raised/72 px-3 pr-12 font-ui text-base text-ink-1 outline-none transition-[background-color,border-color,box-shadow] placeholder:text-ink-4 focus:border-walnut focus:bg-paper-raised focus:shadow-[0_0_0_3px_rgb(var(--as-walnut-rgb)/0.18)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-0 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center text-ink-3 hover:text-walnut-deep"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlash size={21} /> : <Eye size={21} />}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex min-h-10 items-center gap-3 font-text text-sm text-ink-2">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-6 w-6 accent-[var(--as-walnut)]"
                />
                Keep me signed in.
              </label>
              <span className="font-text text-sm text-ink-3">
                {remember ? "Stay signed in" : "Session only"}
              </span>
            </div>
            <Button type="submit" variant="walnut" size="lg" disabled={submitting} className="w-full">
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <Button onClick={handleGoogle} variant="secondary" size="lg" disabled={googleSubmitting} className="mt-3 w-full gap-3">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-paper font-ui text-sm font-semibold text-walnut-deep">G</span>
            {googleSubmitting ? "Opening Google..." : "Sign in with Google"}
          </Button>

          <div className="mt-5 text-center font-text text-sm text-ink-2">
            <span>
              New to PullPress?{" "}
                <Link to={packageIntent ? `/signup?package=${packageIntent}` : "/signup"} className="inline-flex min-h-10 items-center text-walnut-deep underline-offset-2 hover:underline">Create an account</Link>
              </span>
          </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function normalizePackageIntent(value: string | null): string | null {
  if (value === "editorial" || value === "studio" || value === "publishing" || value === "professional") return "editorial";
  if (value === "self-serve" || value === "creator" || value === "production" || value === "essentials") return "self-serve";
  return null;
}

function authRedirectPath(value: unknown): string | null {
  if (!isRecord(value) || !isRecord(value.from)) return null;
  const pathname = value.from.pathname;
  const search = value.from.search;
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return null;
  return `${pathname}${typeof search === "string" ? search : ""}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
